<template>
  <v-app class="signup__bg">
    <v-container fill-height>
      <v-row justify="center" align="center">
        <v-col cols="12" md="5" lg="4">
          <v-window v-model="step">
            <!-- Step 1: Name & Email -->
            <v-window-item :value="1">
              <div class="d-flex justify-center align-center animate-fadeInUp">
                <v-card flat color="transparent">
                  <div class="d-flex justify-center mb-4">
                    <img src="../assets/bglogo.svg" alt="janguAsk logo" width="150" class="animate-float" />
                  </div>
                  <v-card max-width="400" width="100%" class="premium-card-elevated pa-2 mx-auto">
                    <v-card-text>
                      <div class="mb-4">
                        <h3 class="black--text" style="font-size: 22px; font-weight: 700">
                          Create Account
                        </h3>
                        <p class="grey--text text--darken-1 mb-0" style="font-size: 14px">
                          Join the janguAsk learning community
                        </p>
                      </div>

                      <!-- Step indicator -->
                      <div class="d-flex align-center mb-4">
                        <div class="step-dot active"></div>
                        <div class="step-line"></div>
                        <div class="step-dot"></div>
                      </div>

                      <v-form @submit.prevent ref="form1" lazy-validation>
                        <v-col cols="12" class="py-1 px-0">
                          <v-text-field
                            outlined
                            dense
                            rounded
                            prepend-inner-icon="mdi-account-outline"
                            placeholder="Full name"
                            :rules="[rules.required, rules.counter]"
                            v-model="fullName"
                            color="#f69227"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" class="py-0 px-0">
                          <v-text-field
                            outlined
                            dense
                            rounded
                            prepend-inner-icon="mdi-email-outline"
                            type="email"
                            :rules="[rules.required, rules.email]"
                            placeholder="Email address"
                            v-model="email"
                            color="#f69227"
                          ></v-text-field>
                        </v-col>
                      </v-form>

                      <v-col cols="12" class="py-2 px-0">
                        <v-btn
                          block
                          height="48"
                          rounded
                          large
                          color="#f69227"
                          class="white--text text-capitalize hover-scale hover-glow-orange"
                          style="font-weight: 600; font-size: 15px"
                          @click="nextStep"
                        >
                          Continue
                          <v-icon right small>mdi-arrow-right</v-icon>
                        </v-btn>
                      </v-col>

                      <div class="d-flex align-center my-3">
                        <v-divider></v-divider>
                        <span class="mx-3 grey--text" style="font-size: 12px; white-space: nowrap">or sign up with</span>
                        <v-divider></v-divider>
                      </div>

                      <v-row no-gutters>
                        <v-col cols="6" class="pr-1">
                          <v-btn
                            block outlined rounded height="44"
                            class="text-capitalize hover-scale"
                            color="#4267B2"
                            @click="socialLogin('Facebook')"
                          >
                            <v-icon left small>mdi-facebook</v-icon>
                            Facebook
                          </v-btn>
                        </v-col>
                        <v-col cols="6" class="pl-1">
                          <v-btn
                            block outlined rounded height="44"
                            class="text-capitalize hover-scale"
                            color="#DB4437"
                            @click="socialLogin('Google')"
                          >
                            <v-icon left small>mdi-google</v-icon>
                            Google
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                  <p class="d-flex align-center justify-center mt-4" style="font-size: 14px; color: #666">
                    Already have an account?
                    <v-btn text to="/login" small color="#f69227" class="text-capitalize ml-1" style="font-weight: 600">Login</v-btn>
                  </p>
                </v-card>
              </div>
            </v-window-item>

            <!-- Step 2: Password & Phone -->
            <v-window-item :value="2">
              <div class="d-flex justify-center align-center animate-fadeInUp">
                <v-card flat color="transparent">
                  <div class="d-flex justify-center mb-4">
                    <img src="../assets/bglogo.svg" alt="janguAsk logo" width="150" class="animate-float" />
                  </div>
                  <v-card max-width="400" width="100%" class="premium-card-elevated pa-2 mx-auto">
                    <v-card-text>
                      <div class="mb-2">
                        <v-btn icon small @click="step = 1" class="mb-2">
                          <v-icon>mdi-arrow-left</v-icon>
                        </v-btn>
                        <h3 class="black--text" style="font-size: 22px; font-weight: 700">
                          Secure Your Account
                        </h3>
                        <p class="grey--text text--darken-1 mb-0" style="font-size: 14px">
                          Set a strong password
                        </p>
                      </div>

                      <!-- Step indicator -->
                      <div class="d-flex align-center mb-4">
                        <div class="step-dot completed">
                          <v-icon x-small color="white">mdi-check</v-icon>
                        </div>
                        <div class="step-line active"></div>
                        <div class="step-dot active"></div>
                      </div>

                      <v-form @submit.prevent ref="form2" lazy-validation>
                        <v-col cols="12" class="py-1 px-0">
                          <v-text-field
                            outlined
                            dense
                            rounded
                            prepend-inner-icon="mdi-lock-outline"
                            placeholder="Password"
                            v-model="password"
                            :type="showPassword ? 'text' : 'password'"
                            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append="showPassword = !showPassword"
                            color="#f69227"
                            :rules="[rules.required, rules.password]"
                            hide-details="auto"
                          ></v-text-field>

                          <!-- Password strength -->
                          <div class="mt-2 mb-1">
                            <v-progress-linear
                              :value="passwordStrength"
                              :color="passwordStrengthColor"
                              height="4"
                              rounded
                            ></v-progress-linear>
                            <span class="caption" :style="{ color: passwordStrengthColor }">
                              {{ passwordStrengthText }}
                            </span>
                          </div>
                        </v-col>
                        <v-col cols="12" class="py-1 px-0">
                          <v-text-field
                            outlined
                            dense
                            rounded
                            prepend-inner-icon="mdi-lock-check-outline"
                            :type="showPassword ? 'text' : 'password'"
                            color="#f69227"
                            :rules="[rules.required, rules.password, passwordConfirmationRule]"
                            placeholder="Confirm password"
                            v-model="repeatpassword"
                            hide-details="auto"
                          ></v-text-field>
                        </v-col>

                        <v-col cols="12" class="py-2 px-0">
                          <v-card class="pa-1" outlined rounded="lg">
                            <VuePhoneNumberInput
                              v-model="contry"
                              default-country-code="NG"
                            />
                          </v-card>
                        </v-col>
                      </v-form>

                      <v-col cols="12" class="px-0 mt-2">
                        <v-btn
                          block
                          large
                          rounded
                          height="48"
                          color="#f69227"
                          class="white--text text-capitalize hover-scale hover-glow-orange"
                          style="font-weight: 600; font-size: 15px"
                          :loading="isLoading"
                          :disabled="isLoading"
                          @click="signUp()"
                        >
                          <v-icon left small>mdi-account-plus</v-icon>
                          Create Account
                        </v-btn>
                      </v-col>
                    </v-card-text>
                  </v-card>
                  <p class="d-flex align-center justify-center mt-4" style="font-size: 14px; color: #666">
                    Already have an account?
                    <v-btn text to="/login" small color="#f69227" class="text-capitalize ml-1" style="font-weight: 600">Login</v-btn>
                  </p>
                </v-card>
              </div>
            </v-window-item>
          </v-window>
        </v-col>
      </v-row>
    </v-container>
    <v-footer color="transparent" padless absolute>
      <v-row justify="center" no-gutters>
        <img src="../assets/logo grey.svg" width="70" alt="logo" class="mx-1" />
        <p class="rights grey--text" style="font-size: 11px">
          <span class="mx-1">&copy;</span>{{ new Date().getFullYear() }} janguAsk
        </p>
      </v-row>
    </v-footer>
  </v-app>
</template>

<script>
import VuePhoneNumberInput from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";

export default {
  components: {
    VuePhoneNumberInput,
  },
  data: () => ({
    step: 1,
    showPassword: false,
    fullName: "",
    email: "",
    password: "",
    repeatpassword: "",
    contry: "",
    isLoading: false,
    rules: {
      required: (v) => !!v || "This field is required",
      counter: (v) => (v && v.length >= 3) || "Minimum length is 3 characters",
      email: (value) => {
        const pattern =
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return pattern.test(value) || "Please enter a valid email address";
      },
      password: (value) =>
        (value && value.length >= 6) || "Password must be at least 6 characters",
    },
  }),
  computed: {
    passwordConfirmationRule() {
      return () =>
        this.password === this.repeatpassword || "Passwords do not match";
    },
    passwordStrength() {
      if (!this.password) return 0;
      let score = 0;
      if (this.password.length >= 6) score += 25;
      if (this.password.length >= 10) score += 25;
      if (/[A-Z]/.test(this.password)) score += 25;
      if (/[0-9!@#$%^&*]/.test(this.password)) score += 25;
      return score;
    },
    passwordStrengthColor() {
      if (this.passwordStrength <= 25) return "#ef5350";
      if (this.passwordStrength <= 50) return "#ff9800";
      if (this.passwordStrength <= 75) return "#2196f3";
      return "#43a047";
    },
    passwordStrengthText() {
      if (!this.password) return "";
      if (this.passwordStrength <= 25) return "Weak";
      if (this.passwordStrength <= 50) return "Fair";
      if (this.passwordStrength <= 75) return "Good";
      return "Strong";
    },
  },
  methods: {
    nextStep() {
      if (this.$refs.form1.validate()) {
        this.step = 2;
      }
    },
    signUp() {
      if (!this.$refs.form2.validate()) {
        return;
      }
      this.isLoading = true;
      setTimeout(() => {
        this.isLoading = false;
        this.$root.store.signup({
          fullName: this.fullName,
          email: this.email,
        });
        this.$root.showSnackbar(
          "Account created successfully! Welcome to janguAsk!",
          "#43a047",
          "mdi-check-circle"
        );
        setTimeout(() => {
          this.$router.push("/home");
        }, 800);
      }, 2000);
    },
    socialLogin(provider) {
      this.$root.showSnackbar(
        `${provider} sign-up is not available at the moment. Please use email registration.`,
        "#ef5350",
        "mdi-alert-circle"
      );
    },
  },
};
</script>

<style scoped>
.signup__bg {
  background: linear-gradient(135deg, #fafbfc 0%, #f0f2f5 50%, #fef6ec 100%);
  min-height: 100vh;
}

.step-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.step-dot.active {
  background: #f69227;
  box-shadow: 0 0 0 4px rgba(246, 146, 39, 0.2);
}

.step-dot.completed {
  background: #43a047;
}

.step-line {
  height: 3px;
  flex-grow: 1;
  background: #e0e0e0;
  margin: 0 8px;
  border-radius: 2px;
  transition: all 0.3s ease;
}

.step-line.active {
  background: linear-gradient(90deg, #43a047, #f69227);
}
</style>
